/**
 * LotMovementsDrawer (NOVA-058) — the per-lot append-only movement history. Read-only: it lists the
 * MouvementStock ledger for one lot in insert order (entree / sortie / ajustement / peremption) with the
 * signed quantity and the running balance AFTER each row (recomputed server-side, never trusted from a
 * stored projection). Drives 4 of the 5 states inline (offline/loading/error/empty); ForbiddenState is
 * route-level. Gated by inventory.lot.read (the same read cap as the lots list).
 *
 * Data source: GET /inventory/lots/:id/mouvements.
 */
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useT, useUiStore } from "@/app";
import { useLabId, useLotMovements } from "../api";
import type { LotMouvement } from "../types";
import { mouvementVisual } from "../lib";

export interface LotMovementsDrawerProps {
  open: boolean;
  /** The lot whose ledger to show (id + numero for the title). */
  lotId: string | null;
  numeroLot?: string;
  onClose: () => void;
}

/** Locale-aware short timestamp (date + time); the wire carries an ISO string. */
function fmtAt(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar" : "fr", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

export function LotMovementsDrawer({ open, lotId, numeroLot, onClose }: LotMovementsDrawerProps) {
  const { t, locale } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);

  const query = useLotMovements(labId, lotId, open && online && !!lotId);
  const rows = query.data?.mouvements ?? [];

  const columns: ReadonlyArray<Column<LotMouvement>> = [
    {
      key: "at",
      header: t("inventory.movements.col.at"),
      mono: true,
      cell: (m) => <span className="nv-ltr-island">{fmtAt(m.created_at, locale)}</span>,
    },
    {
      key: "type",
      header: t("inventory.movements.col.type"),
      cell: (m) => {
        const v = mouvementVisual(m.type);
        return (
          <Badge tone={v.tone} glyph={v.glyph}>
            {t(v.labelKey)}
          </Badge>
        );
      },
    },
    {
      key: "quantite",
      header: t("inventory.movements.col.quantite"),
      mono: true,
      align: "end",
      cell: (m) => (
        <span className="nv-ltr-island">
          {m.quantite > 0 ? `+${m.quantite}` : String(m.quantite)}
        </span>
      ),
    },
    {
      key: "solde",
      header: t("inventory.movements.col.solde"),
      mono: true,
      align: "end",
      cell: (m) => <span className="nv-ltr-island">{m.solde_apres}</span>,
    },
    {
      key: "motif",
      header: t("inventory.movements.col.motif"),
      cell: (m) => m.motif ?? "—",
    },
  ];

  const body = () => {
    if (!online) return <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />;
    if (query.isLoading) return <LoadingState variant="table" rows={4} />;
    if (query.isError)
      return (
        <ErrorState
          message={query.error.message}
          code={query.error.code}
          onRetry={() => void query.refetch()}
        />
      );
    if (rows.length === 0)
      return (
        <EmptyState
          glyph="▤"
          title={t("inventory.movements.empty.title")}
          description={t("inventory.movements.empty.desc")}
        />
      );
    return (
      <Table
        columns={columns}
        rows={rows}
        rowKey={(m) => m.id}
        caption={t("inventory.movements.title")}
        pageSize={15}
      />
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
          {t("inventory.movements.title")}
          {numeroLot && <Badge tone="info">{numeroLot}</Badge>}
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("inventory.movements.subtitle")}</p>
        {body()}
      </div>
    </Drawer>
  );
}
